import { ChangeEvent, FC, useState } from "react";
import { useAtomValue } from "jotai";

import { TaskDriverStatusEnum } from "@/domain/models/common";
import { ServicesType } from "@/domain/models/job";

import { useUpdateServiceMutation } from "../data/mutations/serviceMutations";
import { useUpdateTaskMutation } from "../data/mutations/taskMutations";
import { useServiceQuery } from "../data/queries/serviceQueries";
import { JobServiceTaskBaseType } from "../domain/task";
import { isDriverReviewingTaskAtom } from "../stores/atoms";
import { DriverScreenWithTextInput } from "./DriverScreenWithTextInput";

const DriverFillingSQFTCleaned: FC<JobServiceTaskBaseType> = ({
  jobId,
  serviceId,
  taskId,
}) => {
  const { data: serviceData } = useServiceQuery({ jobId, serviceId });

  const [sqftCleaned, setSqftCleaned] = useState<number | undefined>(
    serviceData?.sqft ?? undefined,
  );

  const isDriverReviewingTask = useAtomValue(isDriverReviewingTaskAtom);

  const {
    mutate: updateService,
    isPending: isUpdatingService,
    variables,
  } = useUpdateServiceMutation();
  const { mutate: updateTask, isPending: isUpdatingTask } =
    useUpdateTaskMutation();

  const onNextClick = () => {
    const driverFlowStatus = isDriverReviewingTask
      ? TaskDriverStatusEnum.TASK_REVIEW
      : TaskDriverStatusEnum.TAKING_PHOTOS_AREAS_CLEANED;

    const serviceParams: Partial<ServicesType> = {
      jobId,
      driverFlowStatus,
    };

    if (sqftCleaned !== undefined) {
      serviceParams.sqft = sqftCleaned;
    }

    updateService({
      serviceId,
      ...serviceParams,
    });

    updateTask({
      jobId,
      serviceId,
      taskId,
      driverFlowStatus,
    });
  };

  const isLoading = isUpdatingService || isUpdatingTask;

  const isNextButtonLoading =
    isLoading &&
    variables?.driverFlowStatus ===
      TaskDriverStatusEnum.TAKING_PHOTOS_AREAS_CLEANED;
  const isPreviousButtonLoading =
    isLoading &&
    variables?.driverFlowStatus ===
      TaskDriverStatusEnum.FILLING_INTERIOR_CLEANUP_TYPE;

  const goToPreviousStep = () => {
    updateService({
      jobId,
      serviceId,
      driverFlowStatus: TaskDriverStatusEnum.FILLING_INTERIOR_CLEANUP_TYPE,
    });

    updateTask({
      jobId,
      serviceId,
      taskId,
      driverFlowStatus: TaskDriverStatusEnum.FILLING_INTERIOR_CLEANUP_TYPE,
    });
  };

  const handleOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSqftCleaned(Number(event.target.value));
  };

  return (
    <DriverScreenWithTextInput
      jobId={jobId}
      serviceId={serviceId}
      taskId={taskId}
      title="driver_filling_sqft_cleaned.title"
      inputProps={{
        value: sqftCleaned,
        onChange: handleOnChange,
        inputMode: "numeric",
      }}
      bottomButtonProps={{
        isNextButtonLoading,
        nextButtonTitle: isDriverReviewingTask ? "review" : "next",
        onNextButtonClick: onNextClick,
        isNextButtonDisabled: sqftCleaned === undefined,
        previousButtonTitle: "driver_filling_sqft_cleaned.back_button",
        onPreviousButtonClick: isDriverReviewingTask
          ? undefined
          : () => goToPreviousStep(),
        isPreviousButtonLoading,
      }}
    />
  );
};

export { DriverFillingSQFTCleaned };
